/**
 * /auth/request — magic-link request page. Per UX-AC-001 / AC-001..AC-003.
 *
 * Single email field, one primary button. On submit, the same view transitions
 * (no navigation) to the "Check your inbox" caption. Editorial banner copy
 * surfaced from `?expired=1`, `?used=1`, or `?return_to=`.
 */
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { TextInput } from '../../components/ui/TextInput';
import { useRequestMagicLink } from '../../lib/queries';
import { cn } from '../../lib/cn';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function RequestMagicLink() {
  const [params] = useSearchParams();
  const expired = params.get('expired') === '1';
  // Backend redirects to `?reused=1` on a previously-consumed token. Accept
  // either `reused` (canonical, per Agent A contract) or `used` (legacy).
  const reused = params.get('reused') === '1' || params.get('used') === '1';
  const sessionExpired = params.get('session') === 'expired';
  const initialEmail = params.get('email') ?? '';
  const returnTo = params.get('return_to') ?? undefined;

  const [email, setEmail] = React.useState(initialEmail);
  const [touched, setTouched] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const request = useRequestMagicLink();

  const isValid = EMAIL_RE.test(email.trim());
  const showError = touched && !isValid;

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched(true);
    if (!isValid) return;
    try {
      await request.mutateAsync({
        email: email.trim(),
        ...(returnTo ? { return_to: returnTo } : {}),
      });
      setSubmitted(true);
    } catch {
      // ApiError is surfaced as toast in higher-level apps; here we keep the
      // form available for retry without panicking.
    }
  }

  return (
    <section className="mx-auto max-w-md w-full pt-20 pb-12 px-6">
      <header className="mb-12 text-center">
        <h1 className="font-serif text-4xl md:text-5xl text-ink-900 leading-tight">
          A private journal for the places you keep.
        </h1>
      </header>

      {expired ? (
        <Banner>That link has expired. We&apos;ll send a fresh one.</Banner>
      ) : null}
      {reused ? (
        <Banner>That link&apos;s been used. Send a new one.</Banner>
      ) : null}
      {sessionExpired ? (
        <Banner>Your session expired. Send a new link.</Banner>
      ) : null}

      {submitted ? (
        <div className="text-center">
          <p className="font-serif italic text-lg text-ink-900 leading-snug">
            Check your inbox — link expires in 15 minutes.
          </p>
          <button
            type="button"
            className="mt-6 text-xs uppercase tracking-widest text-ink-500 hover:text-ink-900 focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
            onClick={() => setSubmitted(false)}
          >
            Send to a different email
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-8" noValidate>
          <label className="block">
            <span className="font-sans text-xs uppercase tracking-[0.08em] text-ink-500 mb-2 block">
              Your email
            </span>
            <TextInput
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="you@example.com"
              {...(showError ? { errorText: 'Enter a valid email.' } : {})}
              className="text-lg"
              required
            />
          </label>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={request.isPending}
            loadingLabel="Sending…"
            disabled={!isValid || request.isPending}
          >
            Send link
          </Button>
        </form>
      )}
    </section>
  );
}

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'mb-8 px-4 py-3 border border-ink-200 bg-surface',
        'font-serif italic text-sm text-ink-700 leading-snug',
      )}
      role="status"
    >
      {children}
    </div>
  );
}
